/* Vibed Cart Drawer controller */
(function () {
  'use strict';

  var SELECTORS = {
    drawer: '#vibed-cart',
    items: '[data-cart-items]',
    empty: '[data-cart-empty]',
    footer: '[data-cart-footer]',
    subtotal: '[data-cart-subtotal]',
    template: '#vibed-cart-line-template',
    shippingBar: '[data-shipping-bar]',
    shippingMsg: '[data-shipping-msg]',
    shippingFill: '[data-shipping-fill]'
  };

  var drawer = null;
  var planCache = Object.create(null); // handle -> /products/<handle>.js
  var inflight = false;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function money(cents) {
    var n = Math.round(cents) / 100;
    return '$' + n.toFixed(2);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }

  function getProductByHandle(handle) {
    if (planCache[handle]) return Promise.resolve(planCache[handle]);
    return fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) { planCache[handle] = p; return p; });
  }

  function findVariantInProduct(product, variantId) {
    if (!product || !product.variants) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === variantId) return product.variants[i];
    }
    return null;
  }

  // Per-variant selling-plan resolution.
  // Returns { planId, discountPct } for the FIRST plan available to this variant,
  // or null if the variant has no subscription option.
  // This is the bug-fix: we read from the variant's selling_plan_allocations,
  // not from product.selling_plan_groups[0].selling_plans[0] (the SlideCart bug).
  function resolveVariantPlan(product, variantId) {
    var v = findVariantInProduct(product, variantId);
    if (!v || !v.selling_plan_allocations || !v.selling_plan_allocations.length) return null;
    var alloc = v.selling_plan_allocations[0];
    var plan = alloc.selling_plan_id;
    // Discount % — Shopify exposes price_adjustments on plan; we surface first-cycle discount.
    // The /products/<handle>.js endpoint includes the full plan via `selling_plan_groups`.
    var pct = computePctFromAllocation(alloc, v.price);
    if (pct === null) {
      // Fallback: derive % from compare_at vs allocation price
      pct = Math.round(((v.price - alloc.price) / v.price) * 100);
    }
    return { planId: plan, discountPct: pct };
  }

  function computePctFromAllocation(alloc, basePrice) {
    if (!alloc || typeof alloc.price !== 'number' || !basePrice) return null;
    var pct = Math.round(((basePrice - alloc.price) / basePrice) * 100);
    if (isNaN(pct) || pct <= 0) return null;
    return pct;
  }

  function renderShippingBar(cart) {
    if (!drawer) return;
    var bar = $(SELECTORS.shippingBar, drawer);
    if (!bar) return;
    var threshold = parseInt(drawer.getAttribute('data-free-shipping-threshold'), 10) || 0;
    if (threshold <= 0 || cart.item_count === 0) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;
    // Free shipping qualifies at the threshold OR when any line is a subscription
    // (subscriptions ship free via the ReCharge delivery profile).
    var hasSub = (cart.items || []).some(function (l) { return l.selling_plan_allocation; });
    var qualified = hasSub || cart.total_price >= threshold;
    var pct = qualified ? 100 : Math.min(100, Math.round((cart.total_price / threshold) * 100));
    var fill = $(SELECTORS.shippingFill, bar);
    var msg = $(SELECTORS.shippingMsg, bar);
    if (fill) fill.style.width = pct + '%';
    if (msg) {
      if (qualified) {
        msg.textContent = drawer.dataset.unlockedMsg || "🎉 You've unlocked FREE shipping!";
      } else {
        var remaining = money(threshold - cart.total_price);
        var template = drawer.dataset.progressMsg || "You're {{remaining}} away from FREE shipping";
        msg.textContent = template.replace('{{remaining}}', remaining);
      }
    }
  }

  function renderSale(cart) {
    var offer = $('[data-cart-sale]', drawer);
    if (!offer) return;
    var ids = offer.dataset.saleVariants.split(',');
    var lines = cart.items.filter(function (line) {
      return !line.selling_plan_allocation && ids.indexOf(String(line.variant_id)) !== -1;
    });
    var quantity = 0, total = 0, saved = 0;
    lines.forEach(function (line) {
      quantity += line.quantity;
      total += line.final_line_price;
      (line.line_level_discount_allocations || []).forEach(function (allocation) {
        if ((allocation.discount_application.title || '').indexOf('Sober October Sale:') === 0) saved += allocation.amount;
      });
    });
    offer.hidden = !quantity || !saved || Date.now() >= Number(offer.dataset.saleEnd) || cart.currency !== 'USD';
    if (offer.hidden) return;
    $('[data-sale-per-can]', offer).textContent = money(total / (quantity * 24));
    $$('[data-sale-step]', offer).forEach(function (step) {
      var current = Number(step.dataset.saleStep) === Math.min(quantity, 3);
      step.classList.toggle('is-current', current);
      if (current) step.setAttribute('aria-current', 'step');
      else step.removeAttribute('aria-current');
    });
    $('[data-sale-next]', offer).textContent = quantity >= 3
      ? 'Best deal unlocked — 50% off every eligible case.'
      : 'Add 1 more 24-pack to unlock ' + (quantity === 1 ? '$2.62' : '$2.50') + '/can on all your sale cases.';
    $('[data-sale-shop]', offer).hidden = quantity >= 3;
  }

  // Shows the regular price struck through beside the price actually charged
  // whenever Shopify reports a discount on the line or the cart.
  function setPrice(el, original, final) {
    el.textContent = '';
    if (original > final) {
      var label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = 'Regular price ';
      var was = document.createElement('s');
      was.className = 'vibed-cart__price-was';
      was.textContent = money(original);
      var saleLabel = document.createElement('span');
      saleLabel.className = 'visually-hidden';
      saleLabel.textContent = ' Sale price ';
      el.appendChild(label);
      el.appendChild(was);
      el.appendChild(saleLabel);
    }
    el.appendChild(document.createTextNode(money(final)));
  }

  function renderLine(line, lineIndex, productCacheLookup) {
    var tpl = $(SELECTORS.template);
    if (!tpl) return null;
    var node = tpl.content.firstElementChild.cloneNode(true);
    node.setAttribute('data-line-key', line.key);
    node.setAttribute('data-line-index', String(lineIndex + 1));
    node.setAttribute('data-variant-id', String(line.variant_id));
    node.setAttribute('data-product-handle', line.handle || '');

    var imgLink = node.querySelector('.vibed-cart__item-img-link');
    if (imgLink) imgLink.setAttribute('href', line.url || '#');
    var img = node.querySelector('.vibed-cart__item-img');
    if (img) {
      var src = line.image || line.featured_image && line.featured_image.url || '';
      if (src) img.setAttribute('src', src.replace(/^https?:/, ''));
      img.setAttribute('alt', line.product_title || '');
    }
    var titleEl = node.querySelector('.vibed-cart__item-title');
    if (titleEl) {
      titleEl.textContent = line.product_title || line.title || '';
      titleEl.setAttribute('href', line.url || '#');
    }
    var variantEl = node.querySelector('[data-line-variant]');
    if (variantEl) {
      var variantText = (line.variant_title && line.variant_title !== 'Default Title') ? line.variant_title : '';
      if (line.selling_plan_allocation) {
        variantText = (variantText ? variantText + ' · ' : '') + 'Subscription';
      }
      variantEl.textContent = variantText;
    }
    var qtyEl = node.querySelector('[data-line-qty]');
    if (qtyEl) qtyEl.textContent = String(line.quantity);
    var priceEl = node.querySelector('[data-line-price]');
    if (priceEl) setPrice(priceEl, line.original_line_price, line.final_line_price);

    // Subscribe toggle — only if the variant supports a selling plan.
    var subToggle = node.querySelector('[data-sub-toggle]');
    var subBtn = node.querySelector('[data-sub-btn]');
    var subBtnLabel = node.querySelector('[data-sub-btn-label]');
    var subPct = node.querySelector('[data-sub-pct]');
    var product = productCacheLookup(line.handle);
    if (product) {
      var plan = resolveVariantPlan(product, line.variant_id);
      if (plan && plan.planId) {
        subToggle.hidden = false;
        if (line.selling_plan_allocation) {
          // Subscribed: show a static badge WITH the active discount so it's
          // obvious this line was added as a subscription (12pk = 30%, 24pk = 40%).
          subBtn.setAttribute('data-state', 'subscribed');
          subBtn.removeAttribute('data-action');
          subBtn.removeAttribute('data-sub-btn');
          subBtn.removeAttribute('type');
          subBtn.setAttribute('aria-label', 'Subscribed and saving ' + plan.discountPct + '%');
          subBtnLabel.textContent = 'SUBSCRIBED';
          subPct.textContent = 'SAVE ' + plan.discountPct + '%';
        } else {
          subBtn.setAttribute('data-state', 'available');
          subBtn.setAttribute('data-action', 'add');
          subBtn.setAttribute('data-plan-id', String(plan.planId));
          subBtnLabel.textContent = 'SUBSCRIBE & SAVE';
          subPct.textContent = plan.discountPct + '% OFF';
        }
      }
    }

    return node;
  }

  function render(cart, productMap) {
    if (!drawer) return;
    var itemsEl = $(SELECTORS.items, drawer);
    var emptyEl = $(SELECTORS.empty, drawer);
    var footerEl = $(SELECTORS.footer, drawer);
    var subtotalEl = $(SELECTORS.subtotal, drawer);

    itemsEl.innerHTML = '';

    if (cart.item_count === 0) {
      emptyEl.hidden = false;
      footerEl.hidden = true;
    } else {
      emptyEl.hidden = true;
      footerEl.hidden = false;
      var lookup = function (handle) { return productMap[handle] || null; };
      cart.items.forEach(function (line, i) {
        var node = renderLine(line, i, lookup);
        if (node) itemsEl.appendChild(node);
      });
      if (subtotalEl) setPrice(subtotalEl, cart.original_total_price, cart.total_price);
    }

    renderShippingBar(cart);
    renderSale(cart);
    updateHeaderBadge(cart.item_count);
  }

  function refresh() {
    if (!drawer) return Promise.resolve();
    drawer.classList.add('vibed-cart--loading');
    return getCart().then(function (cart) {
      // Pre-fetch products so subscribe toggles render synchronously
      var handles = {};
      cart.items.forEach(function (l) { if (l.handle) handles[l.handle] = true; });
      var keys = Object.keys(handles);
      return Promise.all(keys.map(function (h) { return getProductByHandle(h).catch(function () { return null; }); }))
        .then(function (products) {
          var map = {};
          keys.forEach(function (h, i) { if (products[i]) map[h] = products[i]; });
          render(cart, map);
        });
    }).catch(function (e) {
      console.warn('[vibed-cart] refresh failed', e);
    }).then(function () {
      drawer.classList.remove('vibed-cart--loading');
    });
  }

  function open() {
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(function () {
      drawer.setAttribute('aria-hidden', 'false');
    });
    document.documentElement.style.overflow = 'hidden';
  }

  function close() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    setTimeout(function () { drawer.hidden = true; }, 320);
  }

  function changeLine(lineKey, quantity, sellingPlan) {
    if (inflight) return Promise.resolve();
    inflight = true;
    var body = { id: lineKey, quantity: quantity };
    if (typeof sellingPlan !== 'undefined') body.selling_plan = sellingPlan;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    }).then(function () {
      return refresh();
    }).catch(function (err) {
      console.warn('[vibed-cart] change failed', err);
    }).then(function () {
      inflight = false;
    });
  }

  function addVariant(variantId, quantity) {
    if (inflight) return Promise.resolve();
    inflight = true;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    }).then(function (r) { return r.json(); })
      .then(function () { return refresh(); })
      .catch(function (e) { console.warn('[vibed-cart] add failed', e); })
      .then(function () { inflight = false; open(); });
  }

  function updateHeaderBadge(count) {
    var bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (bubble) {
      var span = bubble.querySelector('span[aria-hidden="true"]');
      if (span) span.textContent = count;
      bubble.style.display = count > 0 ? '' : 'none';
    } else if (count > 0) {
      // Theme may render bubble only when count>0; let theme handle
    }
  }

  // ---------- Event wiring ----------

  function bindDrawerEvents() {
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-vibed-cart-close]')) { e.preventDefault(); close(); return; }

      var item = t.closest('.vibed-cart__item');
      if (item) {
        var lineKey = item.getAttribute('data-line-key');
        var qtyEl = item.querySelector('[data-line-qty]');
        var qty = parseInt(qtyEl && qtyEl.textContent, 10) || 0;

        if (t.closest('[data-line-qty-up]')) { changeLine(lineKey, qty + 1); return; }
        if (t.closest('[data-line-qty-down]')) { changeLine(lineKey, Math.max(0, qty - 1)); return; }
        if (t.closest('[data-line-remove]')) { changeLine(lineKey, 0); return; }

        var subBtn = t.closest('[data-sub-btn]');
        if (subBtn) {
          var action = subBtn.getAttribute('data-action');
          if (action === 'add') {
            var planId = parseInt(subBtn.getAttribute('data-plan-id'), 10);
            if (planId) changeLine(lineKey, qty, planId);
          }
          // No 'remove' branch — downgrade to one-time is intentionally disabled.
          return;
        }
      }

      var addBtn = t.closest('[data-upsell-add]');
      if (addBtn) {
        var vId = parseInt(addBtn.getAttribute('data-variant-id'), 10);
        if (vId) addVariant(vId, 1);
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') close();
    });
  }

  function bindHeaderTrigger() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a#cart-icon-bubble, a[href$="/cart"]');
      if (!link) return;
      // Only intercept on real navigations (not, e.g., middle-click)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      open();
      refresh();
    });
  }

  // Intercept add-to-cart so any path (form submit, fetch, theme JS) opens the drawer.
  function patchFetch() {
    var origFetch = window.fetch;
    if (!origFetch) return;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      var p = origFetch.apply(this, arguments);
      if (isAdd) {
        p.then(function (resp) {
          if (resp && resp.ok) {
            // Defer slightly so caller's .then() runs first
            setTimeout(function () { open(); refresh(); }, 0);
          }
        }).catch(function () {});
      }
      return p;
    };
  }

  function patchFormSubmit() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      // Already handled by another handler? bail if defaultPrevented.
      if (e.defaultPrevented) return;
      e.preventDefault();
      var fd = new FormData(form);
      // Convert to JSON for /cart/add.js (sections=null avoids extra theme work)
      var body = {};
      fd.forEach(function (v, k) {
        if (k in body) {
          if (!Array.isArray(body[k])) body[k] = [body[k]];
          body[k].push(v);
        } else {
          body[k] = v;
        }
      });
      var atc = form.querySelector('[type="submit"]');
      if (atc) { atc.disabled = true; atc.setAttribute('data-was-text', atc.textContent); atc.textContent = 'ADDING...'; }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      }).then(function () {
        return refresh();
      }).then(function () {
        open();
      }).catch(function (err) {
        console.warn('[vibed-cart] form add failed', err);
        // Fallback: let the form submit traditionally
        form.submit();
      }).then(function () {
        if (atc) {
          atc.disabled = false;
          var prev = atc.getAttribute('data-was-text');
          if (prev) { atc.textContent = prev; atc.removeAttribute('data-was-text'); }
        }
      });
    }, true);
  }

  // When our drawer is live, hijack SlideCartHQ's globals so its open/update
  // calls route into us instead of opening the app's drawer alongside ours.
  // SlideCart loads via a Shopify app embed and its scripts run regardless of
  // theme code, so this neutralization needs to be active and re-applied.
  function neutralizeSlideCart() {
    var noop = function () {};
    var openOurs = function () { try { open(); } catch (e) {} };
    function applyOverrides() {
      try {
        window.SLIDECART_OPEN = openOurs;
        window.SLIDECART_UPDATE = noop;
        window.SLIDECART_CLOSE = function () { try { close(); } catch (e) {} };
        // SLIDECART_LOADED / SLIDECART_UPDATED are callback hooks the app calls;
        // make them no-ops so amp-slidecart-edits.liquid can't drive state changes.
        window.SLIDECART_LOADED = noop;
        window.SLIDECART_UPDATED = noop;
      } catch (e) {}
    }
    applyOverrides();
    // Re-apply after SlideCart's bundle would have loaded (it can clobber globals on init).
    setTimeout(applyOverrides, 500);
    setTimeout(applyOverrides, 1500);
    setTimeout(applyOverrides, 3000);
  }

  function init() {
    drawer = $(SELECTORS.drawer);
    if (!drawer) return;
    neutralizeSlideCart();
    // Pull custom progress messages off the drawer's data-* if section settings emitted them
    var bar = $(SELECTORS.shippingBar, drawer);
    if (bar) {
      drawer.dataset.progressMsg = bar.getAttribute('data-progress-msg') || drawer.dataset.progressMsg || '';
      drawer.dataset.unlockedMsg = bar.getAttribute('data-unlocked-msg') || drawer.dataset.unlockedMsg || '';
    }
    bindDrawerEvents();
    bindHeaderTrigger();
    patchFetch();
    patchFormSubmit();
    // Pre-warm cart (non-blocking, paints subtotal + count if user opens)
    refresh();
    // Expose for debugging
    window.VibedCart = { open: open, close: close, refresh: refresh };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
